import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Copy,
  FilePlus,
  Edit,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import RfpTemplateService from '../../services/rfpTemplateService';

const EMPTY_FORM = {
  _id: '',
  templatePurpose: 'RFP_EOI',
  templateName: '',
  projectType: 'General',
  content: '',
  sectionMappingText: '',
};

const DEFAULT_EOI_HTML = `
<h1>NOTICE INVITING EXPRESSION OF INTEREST (EOI)</h1>

<p>{{authority_name}}</p>
<p>{{address}}</p>

<h2>Introduction</h2>
<p>{{introduction}}</p>

<h2>Project Background</h2>
<p>{{project_background}}</p>

<h2>Qualifying Requirements</h2>
<ul>
  {{#each eligibility_points}}
  <li>{{this}}</li>
  {{/each}}
</ul>

<h2>Detailed Term Sheet</h2>
<table>
  <tr><th>Particular</th><th>Details</th></tr>
  {{#each term_sheet}}
  <tr>
    <td>{{label}}</td>
    <td>{{value}}</td>
  </tr>
  {{/each}}
</table>

<h2>Financial Bid</h2>
<p>{{financial_bid_details}}</p>
`.trim();

const DEFAULT_TEMPLATE_PAYLOAD = {
  templatePurpose: 'RFP_EOI',
  templateName: 'Standard EOI Template',
  projectType: 'EOI',
  content: DEFAULT_EOI_HTML,
  sectionMapping: [
    'introduction',
    'project_background',
    'eligibility_points',
    'term_sheet',
    'financial_bid_details',
  ],
  isActive: true,
};

const mapTemplateToForm = (tpl) => ({
  _id: tpl._id,
  templatePurpose: tpl.templatePurpose || 'RFP_EOI',
  templateName: tpl.templateName || '',
  projectType: tpl.projectType || 'General',
  content: tpl.content || '',
  sectionMappingText: (tpl.sectionMapping || []).join(', '),
});

const TemplateRegistry = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const placeholders = useMemo(
    () => RfpTemplateService.extractPlaceholders(form.content || ''),
    [form.content],
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await RfpTemplateService.getTemplates();
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      showError('Unable to load templates.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormOpen(false);
  };

  const onCreate = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const onEdit = (tpl) => {
    setForm(mapTemplateToForm(tpl));
    setFormOpen(true);
  };

  const onSave = async () => {
    if (!form.templatePurpose.trim()) {
      showError('Template Purpose is required.');
      return;
    }

    if (!form.templateName.trim()) {
      showError('Template Name is required.');
      return;
    }

    if (!form.content.trim()) {
      showError('Content is required.');
      return;
    }

    const payload = {
      templatePurpose: form.templatePurpose.trim(),
      templateName: form.templateName.trim(),
      projectType: form.projectType.trim() || 'General',
      content: form.content,
      placeholders,
      sectionMapping: form.sectionMappingText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };

    setSaving(true);
    try {
      if (form._id) {
        await RfpTemplateService.updateTemplate(form._id, {
          ...payload,
          isActive:
            templates.find((t) => t._id === form._id)?.isActive || false,
        });
        success('Template updated.');
      } else {
        await RfpTemplateService.createTemplate(payload);
        success('Template created.');
      }
      resetForm();
      loadTemplates();
    } catch (err) {
      showError(err.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const onAddDefaultTemplate = async () => {
    const alreadyExists = templates.some(
      (tpl) =>
        String(tpl.templatePurpose || '').toUpperCase() === 'RFP_EOI' &&
        String(tpl.templateName || '').trim() === 'Standard EOI Template',
    );

    if (alreadyExists) {
      success('Default template already exists in Template Master.');
      return;
    }

    try {
      await RfpTemplateService.createTemplate({
        ...DEFAULT_TEMPLATE_PAYLOAD,
        placeholders: RfpTemplateService.extractPlaceholders(DEFAULT_EOI_HTML),
      });
      success('Default template added and stored with full HTML sections.');
      loadTemplates();
    } catch (err) {
      showError(err.message || 'Failed to add default template.');
    }
  };

  const onClone = async (tpl) => {
    try {
      await RfpTemplateService.cloneTemplate(tpl._id);
      success('Template cloned.');
      loadTemplates();
    } catch (err) {
      showError(err.message || 'Clone failed.');
    }
  };

  const onDelete = async (tpl) => {
    const confirmed = window.confirm(`Delete template "${tpl.templateName}"?`);
    if (!confirmed) return;

    try {
      await RfpTemplateService.deleteTemplate(tpl._id);
      success('Template deleted.');
      loadTemplates();
    } catch (err) {
      showError(err.message || 'Delete failed.');
    }
  };

  const onActivate = async (tpl) => {
    try {
      await RfpTemplateService.activateTemplate(tpl._id);
      success('Template activated.');
      loadTemplates();
    } catch (err) {
      showError(err.message || 'Activation failed.');
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Template Registry Screen"
        description="Manage reusable RFP templates and placeholder mappings for rfpTemplates"
        actions={[
          {
            icon: (
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            ),
            variant: 'outline',
            onClick: loadTemplates,
          },
          {
            label: 'Create',
            icon: <Plus size={16} />,
            variant: 'primary',
            onClick: onCreate,
          },
          {
            label: 'Add Default Template',
            icon: <FilePlus size={16} />,
            variant: 'secondary',
            onClick: onAddDefaultTemplate,
          },
        ]}
      />

      <div className="page-container">
        {formOpen && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              {form._id ? 'Edit Template' : 'Create Template'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Template Purpose
                </label>
                <input
                  value={form.templatePurpose}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      templatePurpose: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="RFP_EOI"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Prompt Name
                </label>
                <input
                  value={form.templateName}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      templateName: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="Template Name"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Project Type
                </label>
                <input
                  value={form.projectType}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      projectType: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="Project Type"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Content
              </label>
              <textarea
                value={form.content}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, content: e.target.value }))
                }
                className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[150px]"
                placeholder="Use placeholders like {{projectScope}}, {{deadline}}, {{budget}}"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Placeholders ({'{{variable}}'})
              </label>
              <div className="flex flex-wrap gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 min-h-[44px]">
                {placeholders.length ? (
                  placeholders.map((ph) => (
                    <span
                      key={ph}
                      className="px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-700 font-semibold"
                    >
                      {'{{'}
                      {ph}
                      {'}}'}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">
                    No placeholders detected.
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Section Mapping
              </label>
              <input
                value={form.sectionMappingText}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    sectionMappingText: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                placeholder="overview, scope, technical, eligibility, commercial"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving...' : form._id ? 'Edit' : 'Create'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-3 px-4">Template Purpose</th>
                  <th className="py-3 px-4">Template Name</th>
                  <th className="py-3 px-4">Project Type</th>
                  <th className="py-3 px-4">Content</th>
                  <th className="py-3 px-4">Placeholders</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tpl) => (
                  <tr key={tpl._id} className="border-b align-top">
                    <td className="py-3 px-4 text-gray-700">
                      {tpl.templatePurpose || '-'}
                    </td>
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{tpl.templateName}</span>
                        {tpl.isActive && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-bold uppercase">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {tpl.projectType}
                    </td>
                    <td className="py-3 px-4 text-gray-700 max-w-[340px]">
                      <div className="line-clamp-3">{tpl.content}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {(tpl.placeholders || []).length
                        ? (tpl.placeholders || []).join(', ')
                        : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          title="Edit"
                          onClick={() => onEdit(tpl)}
                          className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          title="Clone"
                          onClick={() => onClone(tpl)}
                          className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          title="Activate"
                          onClick={() => onActivate(tpl)}
                          className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                        >
                          <CheckCircle2 size={14} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => onDelete(tpl)}
                          className="p-2 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!templates.length && (
                  <tr>
                    <td colSpan={6} className="py-5 px-4 text-sm text-gray-500">
                      No templates found. Create your first template in
                      rfpTemplates registry.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default TemplateRegistry;
